import { Form, Formik, FormikErrors, FormikHelpers, FormikProps } from 'formik';
import React, { useEffect } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import { FormCityPicker, FormColumn, FormRow, FormSection, FormInputField, FormReadOnlyInputField, FormPhoneNumberField, FormSelectField, SelectOption, FormCheckboxField, FormCollection } from '@rosenau/rosenau-ui';
import ExpectedTimeFrame, { ExpectedTimeFrameOptions } from './models/ExpectedTimeFrame';
import QuoteRequest from './models/QuoteRequest';
import QuoteRequestItem from './models/QuoteRequestItem';
import QuoteRequestNote from './models/QuoteRequestNote';
import QuoteRequestSource, { QuoteRequestSourceOptions } from './models/QuoteRequestSource';
import withCopyButtonWhenDisabled from './withCopyButtonWhenDisabled';
import { City } from '@rosenau/rosenau-ui/dist/FormCityPicker';

class _InputField extends FormInputField<QuoteRequest> {}
const InputField = withCopyButtonWhenDisabled(_InputField);

class _PhoneNumberField extends FormPhoneNumberField<QuoteRequest> {}
const PhoneNumberField = withCopyButtonWhenDisabled(_PhoneNumberField);

//this disables SpecialInstructions UI element (which might be restored in the nearest future, thus commenting out)
//see also method renderSpecialInstructionsSection
//>> AOkoneshnikov 2023-02-15
//class _TextAreaField extends FormTextAreaField<QuoteRequest> {}
//const TextAreaField = withCopyButtonWhenDisabled(_TextAreaField);

class SelectField extends FormSelectField<QuoteRequest> {}
class CheckboxField extends FormCheckboxField<QuoteRequest> {}
class CityPicker extends FormCityPicker<QuoteRequest> {}
class Collection extends FormCollection<QuoteRequestItem> {}

export interface QuoteRequestFormProps {
    data?: QuoteRequest;
    disabled?: boolean;
    renderCitySuggestion?: (city: City | undefined) => React.ReactNode;
}

export interface QuoteRequestFormState {
    yesNoOptions?: SelectOption[];
    provinceOptions?: SelectOption[];
    locationTypeOptions?: SelectOption[];
    freightTypeOptions?: SelectOption[];
    dimensionUnitOptions?: SelectOption[];
    weightUnitOptions?: SelectOption[];
    errorMessage?: React.ReactNode;
    formComplete: boolean;
    formDirty: boolean;
    formValidated: boolean;
    initialValues?: QuoteRequest;
    lastCityValues?: LastCityValues;
    lastCustomerNumber?: string;
}

interface LastCityValues {
    shipperCity?: string;
    shipperProvince?: string;
    consigneeCity?: string;
    consigneeProvince?: string;
}

export {
    ExpectedTimeFrame,
    ExpectedTimeFrameOptions,
    QuoteRequest,
    QuoteRequestItem,
    QuoteRequestNote,
    QuoteRequestSource,
    QuoteRequestSourceOptions
};

interface QuoteRequestFormObserverProps {
    formik: FormikProps<QuoteRequest>;
    dirty: boolean;
    updateDirty: (dirty: boolean) => void;
}

export const QuoteRequestFormObserver = (props: QuoteRequestFormObserverProps) => {
    useEffect(() => {
        if (props.dirty !== props.formik.dirty) {
            props.updateDirty(props.formik.dirty);
        }
    }, [props.formik, props.formik.dirty]);

    return null;
};

export default abstract class QuoteRequestFormBase<P extends QuoteRequestFormProps = QuoteRequestFormProps> extends React.Component<P, QuoteRequestFormState> {
    ref: React.RefObject<HTMLDivElement>;

    constructor(props: Readonly<P>) {
        super(props);

        this.state = {
            yesNoOptions: [
                { value: true, description: "Yes" },
                { value: false, description: "No" }
            ],
            provinceOptions: [
                { value: "AB", description: "AB" },
                { value: "BC", description: "BC" },
                { value: "MB", description: "MB" },
                { value: "NB", description: "NB" },
                { value: "NL", description: "NL" },
                { value: "NT", description: "NT" },
                { value: "NS", description: "NS" },
                { value: "NU", description: "NU" },
                { value: "ON", description: "ON" },
                { value: "PE", description: "PE" },
                { value: "QC", description: "QC" },
                { value: "SK", description: "SK" },
                { value: "YT", description: "YT" }
            ],
            locationTypeOptions: [
                { value: "Business within city limits", description: "Business within city limits" },
                { value: "Residential address", description: "Residential address" },
                { value: "Outside city limits", description: "Outside city limits" }
            ],
            freightTypeOptions: [
                { value: "Pallet", description: "Pallet" },
                { value: "Tote", description: "Tote" },
                { value: "Drum", description: "Drum" },
                { value: "Bundle", description: "Bundle" },
                { value: "Box", description: "Box" },
                { value: "Pail", description: "Pail" },
                { value: "Crate", description: "Crate" },
                { value: "Envelope", description: "Envelope" },
                { value: "Other", description: "Other" }
            ],
            dimensionUnitOptions: [
                { value: "I", description: "in" },
                { value: "F", description: "ft" },
                { value: "C", description: "cm" },
                { value: "M", description: "m" }
            ],
            weightUnitOptions: [
                { value: "L", description: "lbs" },
                { value: "K", description: "kg" }
            ],
            formComplete: false,
            formDirty: false,
            formValidated: false
        };

        this.ref = React.createRef();
    }

    public componentDidMount() {
        this.loadInitialValues();
    }

    public componentDidUpdate(oldProps: P, _oldState: QuoteRequestFormState) {
        if (this.ref.current) {
            window.requestAnimationFrame(() => {
                if (this.ref.current) {
                    if (this.state.formComplete) {
                        const message = this.ref.current.querySelector(".confirmation-message");

                        if (message) {
                            scrollIntoView(message, { behavior: "smooth", scrollMode: "if-needed", block: "center" });
                        }
                    } else if (this.state.formValidated) {
                        const message = this.ref.current.querySelector(".error-message, .invalid-feedback");

                        if (message) {
                            scrollIntoView(message, { behavior: "smooth", scrollMode: "if-needed", block: "center" });
                        }

                        this.setState({
                            formValidated: false
                        });
                    }
                }
            });
        }

        if (!this.state.initialValues || oldProps.data !== this.props.data) {
            this.loadInitialValues();
        }
    }

    public render() {
        if (!this.state.initialValues) {
            return null;
        }

        return <div ref={this.ref}>
            {!this.state.formComplete ? <Formik
                initialValues={this.state.initialValues}
                onSubmit={this.submit.bind(this)}
                validateOnBlur={false}
                validateOnChange={false}
                validate={this.validate.bind(this)}>
                { formik =>
                    (<Form>
                        <QuoteRequestFormObserver formik={formik} dirty={this.state.formDirty} updateDirty={dirty => this.setState({ formDirty: dirty })} />
                        {this.state.errorMessage && <div className="alert alert-danger mt-2 error-message">{this.state.errorMessage}</div>}
                        {this.renderFormLayout(formik)}
                    </Form>)
                }
            </Formik> : this.renderConfirmation()}
        </div>;
    }

    renderContactInfoSection(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Your contact information">
            <FormRow>
                <FormColumn columns={12}>
                    <InputField labelText="Name" name="contactName" type="text" maxLength={30} required={true} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn columns={8}>
                    <InputField labelText="Company name" name="shipperName" type="text" maxLength={35} required={true} disabled={this.props.disabled} />
                </FormColumn>
                <FormColumn columns={4}>
                    {this.renderCustomerNumberField(formik)}
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn columns={12}>
                    <InputField labelText="Email" name="contactEmail" type="email" maxLength={80} required={true} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn columns={8}>
                    <PhoneNumberField labelText="Phone" name="contactPhoneNumber" type="tel" maxLength={70} required={true} disabled={this.props.disabled} />
                </FormColumn>
                <FormColumn columns={4}>
                    <InputField labelText="Extension" name="contactPhoneExtension" type="text" maxLength={5} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
        </FormSection>;
    }

    renderOriginDestinationSection(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Origin and destination">
            <FormRow>
                <CityPicker formik={formik} environment={process.env.REACT_APP_Environment} cityFieldLabelText="Origin city" cityField="shipperCity" cityFieldColumns={5} provinceField="shipperProvince" provinceFieldColumns={2} required={true} disabled={this.props.disabled} onSelected={city => {
                    this.checkCitiesUpdate(city?.n || "", city?.p || "", formik.values.consigneeCity, formik.values.consigneeProvince);
                }} onBlur={() => {
                    this.checkCitiesUpdate(formik.values.shipperCity, formik.values.shipperProvince, formik.values.consigneeCity, formik.values.consigneeProvince);
                }} onProvinceBlur={() => {
                    this.checkCitiesUpdate(formik.values.shipperCity, formik.values.shipperProvince, formik.values.consigneeCity, formik.values.consigneeProvince);
                }} renderSuggestion={this.props.renderCitySuggestion} />
                <FormColumn columns={5}>
                    <SelectField labelText="Location type" name="shipperLocationType" options={this.state.locationTypeOptions} required={true} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
            <FormRow>
                <CityPicker formik={formik} environment={process.env.REACT_APP_Environment} cityFieldLabelText="Destination city" cityField="consigneeCity" cityFieldColumns={5} provinceField="consigneeProvince" provinceFieldColumns={2} required={true} disabled={this.props.disabled} onSelected={city => {
                    this.checkCitiesUpdate(formik.values.shipperCity, formik.values.shipperProvince, city?.n || "", city?.p || "");
                }} onBlur={() => {
                    this.checkCitiesUpdate(formik.values.shipperCity, formik.values.shipperProvince, formik.values.consigneeCity, formik.values.consigneeProvince);
                }} onProvinceBlur={() => {
                    this.checkCitiesUpdate(formik.values.shipperCity, formik.values.shipperProvince, formik.values.consigneeCity, formik.values.consigneeProvince);
                }} renderSuggestion={this.props.renderCitySuggestion} />
                <FormColumn columns={5}>
                    <SelectField labelText="Location type" name="consigneeLocationType" options={this.state.locationTypeOptions} required={true} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
        </FormSection>;
    }

    renderItemDetailsSection(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Item details">
            <Collection name="items" createItem={this.createEmptyItem} minItems={1} maxItems={5} allowAdd={this.props.disabled ? () => false : undefined} allowRemove={this.props.disabled ? () => false : undefined}>
                { (_arrayHelpers, item, index) => (<React.Fragment>
                    <FormRow>
                        <FormColumn columns={3}>
                            <SelectField labelText="Type of freight" name={`items[${index}].type`} options={this.state.freightTypeOptions} required={true} disabled={this.props.disabled} />
                        </FormColumn>
                        <FormColumn columns={6}>
                            <InputField labelText="Description" name={`items[${index}].description`} maxLength={18} required={true} disabled={this.props.disabled} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <SelectField labelText="Stackable?" name={`items[${index}].stackable`} options={this.state.yesNoOptions} disabled={this.props.disabled} />
                        </FormColumn>
                    </FormRow>
                    <FormRow>
                        <FormColumn columns={3}>
                            <InputField labelText="Number of pieces" name={`items[${index}].pieceCount`} min={1} max={100000} type="number" required={true} disabled={this.props.disabled} onChange={(event: any) => {
                                formik.setFieldValue(`items[${index}].weight` as any, this.getTotalWeightFromPieceCount(parseFloat(event.target.value), item));
                            }} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <InputField labelText="Weight (total)" name={`items[${index}].weight`} min={1} max={100000} step="any" type="number" required={true} disabled={this.props.disabled} onChange={(event: any) => {
                                formik.setFieldValue(`items[${index}].weightPerPiece` as any, this.getWeightPerPieceFromTotal(parseFloat(event.target.value), item) || "");
                            }} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <InputField labelText="Weight (per piece)" name={`items[${index}].weightPerPiece`} min={0} max={100000} step="any" type="number" disabled={this.props.disabled} onChange={(event: any) => {
                                formik.setFieldValue(`items[${index}].weight` as any, this.getTotalWeightFromPerPiece(parseFloat(event.target.value), item) || "");
                            }} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <SelectField labelText="Units" name={`items[${index}].weightUnitCode`} options={this.state.weightUnitOptions} disabled={this.props.disabled} />
                        </FormColumn>
                    </FormRow>
                    <FormRow>
                        <FormColumn columns={3}>
                            <InputField labelText="Length" name={`items[${index}].length`} type="number" required={true} disabled={this.props.disabled} onChange={(event: any) => {
                                const numValue = parseFloat(event.target.value);

                                formik.setFieldValue("longFreight", !isNaN(numValue) && this.convertDimensionToFeet(numValue, item.dimensionUnitCode || "I") > 12);
                            }} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <InputField labelText="Width" name={`items[${index}].width`} type="number" required={true} disabled={this.props.disabled} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <InputField labelText="Height" name={`items[${index}].height`} type="number" required={true} disabled={this.props.disabled} />
                        </FormColumn>
                        <FormColumn columns={3}>
                            <SelectField labelText="Units" name={`items[${index}].dimensionUnitCode`} options={this.state.dimensionUnitOptions} disabled={this.props.disabled} onChange={event => {
                                formik.setFieldValue("longFreight", item.length && this.convertDimensionToFeet(item.length, event.target.value) > 12);
                            }} />
                        </FormColumn>
                    </FormRow>
                    <FormRow>
                        <FormColumn columns={12} className="mt-n2">
                            <small className="form-text text-muted">Specify length, width, and height per piece</small>
                        </FormColumn>
                    </FormRow>
                    <FormRow>
                        <FormColumn columns={6}>
                            <SelectField labelText="Dangerous goods?" name={`items[${index}].dangerousGoods`} options={this.state.yesNoOptions} disabled={this.props.disabled} />
                        </FormColumn>
                    </FormRow>
                </React.Fragment>)}
            </Collection>
        </FormSection>;
    }

    renderFreightTotalsSection(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Freight totals">
            <FormRow>
                <FormColumn columns={4}>
                    <FormReadOnlyInputField labelText="Dangerous goods" value={this.getTotalDangerousGoods(formik.values.items || [])} />
                </FormColumn>
                <FormColumn columns={4}>
                    <FormReadOnlyInputField labelText="Total items" value={this.getTotalPieceCount(formik.values.items || [])} />
                </FormColumn>
                <FormColumn columns={4}>
                    <FormReadOnlyInputField labelText="Total cubic feet" value={this.getTotalCubicFeet(formik.values.items || [])} />
                </FormColumn>
            </FormRow>
            <FormRow>
                <FormColumn columns={6}>
                    <FormReadOnlyInputField labelText="Total weight (kg)" value={this.getTotalWeightInKilograms(formik.values.items || [])} />
                </FormColumn>
                <FormColumn columns={6}>
                    <FormReadOnlyInputField labelText="Total weight (lbs)" value={this.getTotalWeightInPounds(formik.values.items || [])} />
                </FormColumn>
            </FormRow>
        </FormSection>;
    }

    renderRequestedServicesSection(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Requested services">
            <FormRow>
                <FormColumn columns={12}>
                    <CheckboxField labelText="Protect from freezing" name="protectFromFreezing" disabled={this.props.disabled} />
                    <CheckboxField labelText="Flat deck required" name="flatDeckRequired" disabled={this.props.disabled} />
                    <CheckboxField labelText="Long freight > 12 feet" name="longFreight" disabled={this.props.disabled || (this.disableLongFreightWhenMandatory() && this.anyLongFreight(formik))} />
                    <CheckboxField labelText="Power tailgate delivery" name="powerTailgateDelivery" disabled={this.props.disabled} />
                    <CheckboxField labelText="Residential delivery" name="residentialDelivery" disabled={this.props.disabled} />
                    <CheckboxField labelText="Inside delivery" name="insideDelivery" disabled={this.props.disabled} />
                    <CheckboxField labelText="Mall delivery" name="mallDelivery" disabled={this.props.disabled} />
                    <CheckboxField labelText="Saturday delivery" name="saturdayDelivery" disabled={this.props.disabled} />
                    <CheckboxField labelText="Additional insurance" name="additionalInsurance" disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
            {formik.values.additionalInsurance && <FormRow>
                <FormColumn columns={6}>
                    <InputField labelText="Declared valuation" name="declaredValuation" type="number" min="0" max="50000" required={true} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>}
        </FormSection>;
    }

    renderReferenceNumbersSection(_formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormSection title="Reference numbers">
            <FormRow>
                <FormColumn columns={6}>
                    <InputField labelText="Customer reference number" name="referenceNumber" type="text" maxLength={20} disabled={this.props.disabled} />
                </FormColumn>
                <FormColumn columns={6}>
                    <InputField labelText="Purchase Order number" name="purchaseOrderNumber" type="text" maxLength={15} disabled={this.props.disabled} />
                </FormColumn>
            </FormRow>
        </FormSection>;
    }

    renderSpecialInstructionsSection(_formik: FormikProps<QuoteRequest>): React.ReactNode {
         return <div/>;
//this disables SpecialInstructions UI element (which might be restored in the nearest future, thus commenting out)
//see also line 19!!!!!!!!!!
//>> AOkoneshnikov 2023-02-15


      // <FormSection title="Special instructions">
      //       <FormRow>
      //           <FormColumn columns={12}>
      //               <TextAreaField name="specialInstructions" rows={4} disabled={this.props.disabled} />
      //           </FormColumn>
      //       </FormRow>
      //   </FormSection>;
    }

    renderConsent(_formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <FormRow>
            <FormColumn columns={12}>
                <CheckboxField labelText="I'd like to receive relevant updates from you." name="emailConsent" disabled={this.props.disabled} />
            </FormColumn>
        </FormRow>;
    }

    renderFormLayout(formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <React.Fragment>
            {this.renderContactInfoSection(formik)}
            {this.renderOriginDestinationSection(formik)}
            {this.renderItemDetailsSection(formik)}
            {this.renderFreightTotalsSection(formik)}
            {this.renderRequestedServicesSection(formik)}
            {this.renderReferenceNumbersSection(formik)}
            {this.renderSpecialInstructionsSection(formik)}
            {this.renderConsent(formik)}
            {this.renderAdditionalContent(formik)}
            {this.renderButtons(formik)}
        </React.Fragment>;
    }

    createEmptyModel(): QuoteRequest {
        return {
            contactName: "",
            shipperName: "",
            customerNumber: "",
            referenceNumber: "",
            purchaseOrderNumber: "",
            contactEmail: "",
            contactPhoneNumber: "",
            contactPhoneExtension: "",
            shipperCity: "",
            shipperProvince: "",
            shipperLocationType: "Business within city limits",
            consigneeCity: "",
            consigneeProvince: "",
            consigneeLocationType: "Business within city limits",
            items: [
                this.createEmptyItem()
            ],
            protectFromFreezing: false,
            flatDeckRequired: false,
            longFreight: false,
            powerTailgateDelivery: false,
            residentialDelivery: false,
            insideDelivery: false,
            mallDelivery: false,
            saturdayDelivery: false,
            additionalInsurance: false,
            declaredValuation: "" as any,
            emailConsent: false,
            specialInstructions: "",
            __ss_tk: ""
        };
    }

    createEmptyItem(): QuoteRequestItem {
        return {
            pieceCount: "" as any,
            type: "Pallet",
            stackable: "false" as any,
            length: "" as any,
            width: "" as any,
            height: "" as any,
            dimensionUnitCode: "I",
            description: "",
            weight: "" as any,
            weightPerPiece: "" as any,
            weightUnitCode: "L",
            dangerousGoods: "false" as any
        };
    }

    renderAdditionalContent(_formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <React.Fragment></React.Fragment>;
    }

    renderCustomerNumberField(_formik: FormikProps<QuoteRequest>): React.ReactNode {
        return <InputField labelText="Account # (if known)" name="customerNumber" type="text" maxLength={6} disabled={this.props.disabled} onChange={event => this.checkCustomerNumberUpdate(event.target.value)} />;
    }

    loadInitialValues() {
        const initialValues = this.props.data as QuoteRequest || this.createEmptyModel();

        this.setState({
            initialValues: initialValues,
            lastCityValues: {
                shipperCity: initialValues.shipperCity,
                shipperProvince: initialValues.shipperProvince,
                consigneeCity: initialValues.consigneeCity,
                consigneeProvince: initialValues.consigneeProvince
            },
            lastCustomerNumber: initialValues.customerNumber
        });
    }

    checkCitiesUpdate(shipperCity?: string, shipperProvince?: string, consigneeCity?: string, consigneeProvince?: string) {
        if (shipperCity !== this.state.lastCityValues?.shipperCity || shipperProvince !== this.state.lastCityValues?.shipperProvince || consigneeCity !== this.state.lastCityValues?.consigneeCity || consigneeProvince !== this.state.lastCityValues?.consigneeProvince) {
            this.setState({
                lastCityValues: {
                    shipperCity: shipperCity,
                    shipperProvince: shipperProvince,
                    consigneeCity: consigneeCity,
                    consigneeProvince: consigneeProvince
                }
            });

            this.onCitiesUpdated(shipperCity, shipperProvince, consigneeCity, consigneeProvince);
        }
    }

    onCitiesUpdated(_shipperCity?: string, _shipperProvince?: string, _consigneeCity?: string, _consigneeProvince?: string) {
        // Do nothing by default.
    }

    checkCustomerNumberUpdate(customerNumber?: string) {
        if (customerNumber !== this.state.lastCustomerNumber) {
            this.setState({
                lastCustomerNumber: customerNumber
            });

            this.onCustomerNumberUpdated(customerNumber);
        }
    }

    onCustomerNumberUpdated(_customerNumber?: string) {
        // Do nothing by default.
    }

    disableLongFreightWhenMandatory(): boolean {
        return true;
    }

    abstract renderButtons(formik: FormikProps<QuoteRequest>): React.ReactNode;

    abstract renderConfirmation(): React.ReactNode;

    abstract submit(values: QuoteRequest, formik: FormikHelpers<QuoteRequest>): Promise<void>;

    abstract validate(values: QuoteRequest): Promise<FormikErrors<QuoteRequest>>;

    convertWeightToPounds(weight: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "L":
                result = weight;
                break;

            case "K":
                result = weight * 2.205;
                break;

            default:
                throw new Error(`${unit} is not a known unit of weight.`);
        }

        return Math.ceil(result);
    }

    convertWeightToKilograms(weight: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "L":
                result = weight / 2.205;
                break;

            case "K":
                result = weight;
                break;

            default:
                throw new Error(`${unit} is not a known unit of weight.`);
        }

        return Math.ceil(result);
    }

    convertDimensionToFeet(dimension: number, unit: string): number {
        let result: number;

        switch (unit) {
            case "I":
                result = dimension / 12;
                break;

            case "F":
                result = dimension;
                break;

            case "C":
                result = dimension / 30.48;
                break;

            case "M":
                result = dimension * 3.281;
                break;

            default:
                throw new Error(`${unit} is not a known unit of dimension.`);
        }

        return result;
    }

    getWeightPerPieceFromTotal(totalWeight: number | undefined, item: QuoteRequestItem): number | undefined {
        if (!item.pieceCount) {
            return undefined;
        }

        if (!totalWeight || isNaN(totalWeight)) {
            return undefined;
        }

        return Math.round((totalWeight / item.pieceCount) * 10) / 10;
    }

    getTotalWeightFromPerPiece(weightPerPiece: number | undefined, item: QuoteRequestItem): number | undefined {
        if (!item.pieceCount) {
            return undefined;
        }

        if (!weightPerPiece || isNaN(weightPerPiece)) {
            return undefined;
        }

        return weightPerPiece * item.pieceCount;
    }

    getTotalWeightFromPieceCount(pieceCount: number | undefined, item: QuoteRequestItem): number | undefined {
        if (!pieceCount) {
            return undefined;
        }

        if (!item.weightPerPiece) {
            return undefined;
        }

        return item.weightPerPiece * pieceCount;
    }

    getTotalDangerousGoods(items: QuoteRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            if (item.dangerousGoods?.toString() === "true") {
                result++;
            }
        });

        return result;
    }

    getTotalPieceCount(items: QuoteRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += item.pieceCount || 0;
        });

        return result;
    }

    getTotalCubicFeet(items: QuoteRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            const length = this.convertDimensionToFeet(item.length || 0, item.dimensionUnitCode || "I");
            const width = this.convertDimensionToFeet(item.width || 0, item.dimensionUnitCode || "I");
            const height = this.convertDimensionToFeet(item.height || 0, item.dimensionUnitCode || "I");
            const pieceCount = item.pieceCount || 0;

            result += length * width * height * pieceCount;
        });

        return Math.ceil(result);
    }

    getTotalWeightInKilograms(items: QuoteRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += this.convertWeightToKilograms(item.weight || 0, item.weightUnitCode || "L");
        });

        return result;
    }

    getTotalWeightInPounds(items: QuoteRequestItem[]): number {
        let result = 0;

        items.forEach(item => {
            result += this.convertWeightToPounds(item.weight || 0, item.weightUnitCode || "L");
        });

        return result;
    }

    anyLongFreight(formik: FormikProps<QuoteRequest>): boolean {
        let result = false;

        formik.values.items?.forEach(item => {
            if (!item.length || result) {
                return;
            }

            if (this.convertDimensionToFeet(item.length, item.dimensionUnitCode || "I") > 12) {
                result = true;
            }
        });

        return result;
    }
}
